import type { JSX, MouseEvent } from 'react'
import type { SubnavItem, SubnavModel } from '../tabs.js'

/**
 * A screen's secondary navigation: its tabs, as links.
 *
 * ## Links, not an ARIA tablist
 *
 * Each tab is a path (see `routing.ts`), so choosing one *navigates*: the URL
 * changes, the back button returns, and cmd-click opens it in a new tab because
 * the href is real. A `role="tablist"` promises arrow-key movement between tabs
 * and a panel that swaps in place, neither of which is what happens — so this
 * is a `<nav>` of anchors with `aria-current="page"`, the same shape as the
 * main nav, and it needs no keyboard handling of its own: Tab reaches each
 * link, Enter follows it, and `:focus-visible` is the global ring.
 *
 * ## Word and shape, then colour
 *
 * The current tab is bold and carries a solid bar under it; the accent is the
 * third cue. Read on a projector or by someone who cannot tell the accent from
 * the ink, the bar and the weight still say which one this is.
 *
 * ## Narrow screens
 *
 * The list scrolls sideways in its own box rather than wrapping — five tabs
 * wrapped onto two lines read as two rows of navigation.
 */

const follow =
  (to: string, onOpen: (path: string) => void) =>
  (event: MouseEvent<HTMLAnchorElement>): void => {
    // Cmd-, ctrl-, shift- and middle-click keep the browser's own behaviour,
    // which is the whole reason these are anchors.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    onOpen(to)
  }

export const Subnav = ({
  model,
  onOpen,
}: {
  readonly model: SubnavModel
  readonly onOpen: (path: string) => void
}): JSX.Element | null =>
  // One tab is no navigation — see `tabs.ts`.
  model.items.length < 2 ? null : (
    <nav aria-label={model.label} className="subnav">
      <ul>
        {model.items.map((item) => (
          <li key={item.tab}>
            <a
              href={item.to}
              onClick={follow(item.to, onOpen)}
              {...(item.tab === model.current ? { 'aria-current': 'page' as const } : {})}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )

/**
 * A deep link to a tab this viewer's role does not include.
 *
 * Polite and specific, in the words the console already uses for a refusal
 * ("your role does not permit that (capability)"). Unlike a 404 this leaks
 * nothing: it is about the viewer's own role, which they can already see in the
 * header, and says nothing about whether the thing behind the tab exists.
 */
export const TabUnavailable = ({
  needs,
  fallback,
  onOpen,
}: {
  readonly needs: string
  readonly fallback: SubnavItem
  readonly onOpen: (path: string) => void
}): JSX.Element => (
  <section className="panel">
    <p role="status">
      This section is not available to your role ({needs}).{' '}
      <a href={fallback.to} onClick={follow(fallback.to, onOpen)}>
        Go to {fallback.label}
      </a>
    </p>
  </section>
)
